/**
 * Meta Pixel + GTM Analytics Module
 *
 * Implements the exact same pattern as the funnelV3 reference:
 *   App code calls pushMetaEvent() → pushes to window.dataLayer
 *   GTM Web Container catches meta_* events → fires browser pixel (fbq)
 *   GTM Web Container also forwards to sGTM via GA4 tag
 *   sGTM Stape tag sends to Meta CAPI server-side
 *   Browser pixel + CAPI both carry the same event_id → Meta deduplicates
 *
 * Only 4 Meta events: meta_lead, meta_view_content, meta_initiate_checkout, meta_purchase
 */

declare global {
    interface Window {
        dataLayer: any[];
    }
}

// ── Types ────────────────────────────────────────────────────────────

export interface MetaEventPayload {
    meta_event_name: 'Lead' | 'ViewContent' | 'InitiateCheckout' | 'Purchase';
    event_id: string;
    value?: number;
    currency?: string;
    order_id?: string;
    content_name?: string;
    user_data: {
        em?: string;
        fn?: string;
        ln?: string;
        external_id?: string;
        fbp?: string;
        fbc?: string;
        client_user_agent?: string;
    };
    [key: string]: any;
}

// ── Internal: push to dataLayer (NOT exported) ───────────────────────

function pushMetaEvent(eventName: string, payload: MetaEventPayload): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...payload });

    if (process.env.NODE_ENV === 'development') {
        console.log('[MetaAnalytics]', eventName, payload);
    }
}

// ── Random string helper ─────────────────────────────────────────────

function randomStr(): string {
    return Math.random().toString(36).substring(2, 10);
}

// ── Cookie utilities ─────────────────────────────────────────────────

export function getFbpCookie(): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(/(^| )_fbp=([^;]+)/);
    return match ? decodeURIComponent(match[2]) : null;
}

export function getFbcValue(): string | null {
    if (typeof document === 'undefined') return null;

    // 1. Try _fbc cookie
    const cookieMatch = document.cookie.match(/(^| )_fbc=([^;]+)/);
    if (cookieMatch) return decodeURIComponent(cookieMatch[2]);

    // 2. Construct from fbclid URL param
    if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const fbclid = params.get('fbclid');
        if (fbclid) {
            return `fb.1.${Date.now()}.${fbclid}`;
        }
    }

    return null;
}

const FB_COOKIES_KEY = 'mc_fb_cookies';

export function storeFbCookies(): void {
    const fbp = getFbpCookie();
    const fbc = getFbcValue();
    const data: Record<string, string> = {};
    if (fbp) data.fbp = fbp;
    if (fbc) data.fbc = fbc;
    if (Object.keys(data).length > 0) {
        localStorage.setItem(FB_COOKIES_KEY, JSON.stringify(data));
    }
}

export function getStoredFbCookies(): { fbp?: string; fbc?: string } {
    try {
        const raw = localStorage.getItem(FB_COOKIES_KEY);
        if (!raw) return {};
        return JSON.parse(raw);
    } catch {
        return {};
    }
}

// ── Name splitting ───────────────────────────────────────────────────

export function splitFullName(fullName: string): { fn: string; ln: string } {
    const trimmed = (fullName || '').trim();
    if (!trimmed) return { fn: '', ln: '' };
    const parts = trimmed.split(' ');
    return {
        fn: parts[0],
        ln: parts.slice(1).join(' ') || '',
    };
}

// ── Build user_data object ───────────────────────────────────────────

export function buildMetaUserData(
    email?: string,
    fn?: string,
    ln?: string,
    userId?: string
): MetaEventPayload['user_data'] {
    const cookies = getStoredFbCookies();
    return {
        ...(email && { em: email.toLowerCase().trim() }),
        ...(fn && { fn }),
        ...(ln && { ln }),
        ...(userId && { external_id: userId }),
        ...(cookies.fbp && { fbp: cookies.fbp }),
        ...(cookies.fbc && { fbc: cookies.fbc }),
        client_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };
}

// ── Fire-once guard helpers ──────────────────────────────────────────

function hasFireOnceFlag(key: string): boolean {
    return sessionStorage.getItem(key) === '1';
}

function setFireOnceFlag(key: string): void {
    sessionStorage.setItem(key, '1');
}

// ── Track functions ──────────────────────────────────────────────────

/**
 * trackLead — fires on email submit.
 * Fire-once guard: YES (sessionStorage 'fb_fired_lead')
 */
export function trackLead(email: string, userId?: string): void {
    if (hasFireOnceFlag('fb_fired_lead')) return;
    setFireOnceFlag('fb_fired_lead');

    const eventId = `lead_${Date.now()}_${randomStr()}`;
    const userData = buildMetaUserData(email, undefined, undefined, userId);

    pushMetaEvent('meta_lead', {
        meta_event_name: 'Lead',
        event_id: eventId,
        user_data: userData,
    });
}

/**
 * trackInitiateCheckout — fires when user selects a plan on paywall.
 * Fire-once guard: YES (sessionStorage 'fb_fired_initiate_checkout')
 */
export function trackInitiateCheckout(
    planPrice: number,
    currency: string = 'USD',
    email?: string,
    userId?: string
): void {
    if (hasFireOnceFlag('fb_fired_initiate_checkout')) return;
    setFireOnceFlag('fb_fired_initiate_checkout');

    const eventId = `initiate_checkout_${Date.now()}_${randomStr()}`;

    // Try to get email from localStorage if not provided
    const resolvedEmail = email || localStorage.getItem('mc_user_email') || undefined;
    const userData = buildMetaUserData(resolvedEmail, undefined, undefined, userId);

    pushMetaEvent('meta_initiate_checkout', {
        meta_event_name: 'InitiateCheckout',
        event_id: eventId,
        value: planPrice,
        currency,
        user_data: userData,
    });
}

/**
 * trackPurchase — fires on payment confirmed (trial + each upsell).
 * Fire-once guard: NO — each purchase is distinct, Stripe ID guarantees uniqueness.
 * event_id = transactionId (Stripe subscription/payment ID) for deduplication.
 */
export function trackPurchase(
    transactionId: string,
    amount: number,
    currency: string = 'USD',
    planId: string,
    email?: string,
    fullName?: string,
    userId?: string
): void {
    const { fn, ln } = splitFullName(fullName || '');

    const userData = buildMetaUserData(email, fn, ln, userId);

    pushMetaEvent('meta_purchase', {
        meta_event_name: 'Purchase',
        event_id: transactionId,
        value: amount,
        currency,
        order_id: transactionId,
        content_name: planId,
        plan_type: planId,
        user_data: userData,
    });

    // Store user data in localStorage for upsell tracking
    if (email) localStorage.setItem('mc_user_email', email.toLowerCase().trim());
    if (fn) localStorage.setItem('mc_user_fn', fn);
    if (ln) localStorage.setItem('mc_user_ln', ln);
}

/**
 * trackUpsellPurchase — fires for each upsell purchase.
 * Retrieves user_data from localStorage.
 * Fire-once guard: NO.
 */
export function trackUpsellPurchase(
    transactionId: string,
    amount: number,
    currency: string = 'USD',
    planId: string
): void {
    const email = localStorage.getItem('mc_user_email') || undefined;
    const fn = localStorage.getItem('mc_user_fn') || undefined;
    const ln = localStorage.getItem('mc_user_ln') || undefined;
    const cookies = getStoredFbCookies();
    const storedUser = localStorage.getItem('user');
    const userId = storedUser ? (JSON.parse(storedUser)?.id || undefined) : undefined;

    const userData = buildMetaUserData(email, fn, ln, userId);

    pushMetaEvent('meta_purchase', {
        meta_event_name: 'Purchase',
        event_id: transactionId,
        value: amount,
        currency,
        order_id: transactionId,
        content_name: planId,
        plan_type: planId,
        user_data: userData,
    });
}
